// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

export interface TransformationsOptions {
    width?: number | string;
    quality?: "best" | "good" | "eco" | "low";
    variant?: "small" | "medium" | "large";
    mobileWidth?: number;
    tabletWidth?: number;
}

/**
 *
 * @param url: image url
 * @param baseUrl: Cloudinary url domain - include slash at the end
 * @param transformations: image transformation
 */
export const getCloudinaryUrl = (
    url?: string,
    baseUrl?: string,
    transformations?: TransformationsOptions,
): string | undefined => {
    const unNeededUrl = "image/upload/";
    let options = "f_auto";
    let isSvg = false;

    if (!url || !baseUrl || !url.startsWith(baseUrl)) {
        return url;
    }

    let specificUrl = url.replace(baseUrl, "");
    // drop file extention, it will be automatically formatted with f_auto
    const regx = /\.(gif|jpe?g|tiff?|png|webp|bmp)$/;
    if (specificUrl.match(regx)) {
        specificUrl = specificUrl.replace(regx, "");
    }
    // If this is an svg file, we need to remove f_auto option which will
    // rasterize the vector graphic and instead use fl_sanitize to prevent
    // automatically downloading the svg instead of displaying correctly as img src.
    if (url.endsWith(".svg")) {
        isSvg = true;
        options = "fl_sanitize";
    }

    // remove the unused portion of url so options we pass are in the correct order
    if (specificUrl.startsWith(unNeededUrl)) {
        specificUrl = specificUrl.replace(unNeededUrl, "");
    }

    // If transformations are present and this is not a vector graphic, add transformations to options
    if (transformations && Object.keys(transformations).length && !isSvg) {
        if (transformations.width) {
            const width = typeof transformations.width === "string" && transformations.width.endsWith("px") ?
                transformations.width.slice(0,-2) :
                transformations.width;
            options = `${options},w_${width}`;
        }
        if (transformations.quality) {
            options = `${options},q_auto:${transformations.quality}`;
        }
        if (transformations.variant && !transformations.width) {
            options = `${options},t_product-${transformations.variant}`;
        }
        // remove client transformations
        specificUrl = `/${specificUrl.split(/[/,]/).map(option => {
            switch (option) {
                case "t_product-small":
                    return "";
                case "t_product-medium":
                    return "";
                case "t_product-large":
                    return "";
                case "f_auto":
                    return "";
                default:
                    return option;
            }
        }).filter(Boolean).join("/")}`;
    }
    // Remove dublicated transformations
    const optionsParts = options.split(",");
    optionsParts.forEach((option, index) => {
        if (specificUrl.includes(option)) {
            optionsParts[index] = "";
        }
    });
    options = optionsParts.filter(Boolean).join(",");

    // use the specified width in transofromations
    const urlParts = specificUrl.split("/").filter(Boolean);
    const urlOptions = urlParts[0].split(",").filter(Boolean);
    urlOptions.forEach((opt, index) => {
        if (opt.startsWith("w_") && optionsParts.includes(`w_${transformations?.width}`)) {
            urlOptions[index] = `w_${transformations?.width}`;
        }
    });
    urlParts[0] = urlOptions.join(",");
    specificUrl = urlParts.join("/");

    if (baseUrl.endsWith("/") && specificUrl.startsWith("/")) {
        return `${baseUrl}${options}${specificUrl}`;
    }

    if (!baseUrl.endsWith("/") && specificUrl.startsWith("/")) {
        return `${baseUrl}/${options}${specificUrl}`;
    }

    if (baseUrl.endsWith("/") && !specificUrl.startsWith("/")) {
        return `${baseUrl}${options}/${specificUrl}`;
    }

    return `${baseUrl}/${options}/${specificUrl}`;
};
